import React, { useCallback, useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { Document, Page, pdfjs } from "react-pdf";
import styles from "./ProtocolPreview.module.css";
import { ProtocolDocumentButtons } from "src/screens/assemblies/assembly/protocol/ProtocolDocumentButtons";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const pdfDocumentOptions = {
    wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`
};

interface IProtocolPreviewProps {
    assemblyId: string;
}

const ProtocolPreviewBase = observer((props: IProtocolPreviewProps) => {
    const { t } = useTranslation();
    const { protocolStore } = useContext(RootStoreContext);

    const [numPages, setNumPages] = useState<number>(0);
    const [pageWidth, setPageWidth] = useState(window.innerWidth * 0.8);
    const [zoomLevel, setZoomLevel] = useState(1);

    useEffect(() => {
        protocolStore.getAssemblyProtocolFile(props.assemblyId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.assemblyId]);

    useEffect(() => {
        const handleResize = () => {
            const screenWidth = window.innerWidth;
            let newWidth;

            if (screenWidth < 480) {
                newWidth = screenWidth * 0.95;
            } else if (screenWidth < 768) {
                newWidth = screenWidth * 0.9;
            } else if (screenWidth < 1024) {
                newWidth = screenWidth * 0.8;
            } else {
                newWidth = 900;
            }

            setPageWidth(newWidth * zoomLevel);
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [zoomLevel]);

    // Handle Zooming with Ctrl + Scroll
    useEffect(() => {
        const handleWheelZoom = (event: WheelEvent) => {
            if (event.ctrlKey || event.metaKey) {
                // Detect Ctrl (Windows) or Cmd (Mac)
                event.preventDefault(); // Prevent browser zoom
                setZoomLevel((prev) => {
                    const newZoom = event.deltaY < 0 ? prev + 0.1 : prev - 0.1; // Zoom In/Out
                    return Math.min(Math.max(newZoom, 0.5), 3); // Limit between 0.5x and 3x
                });
            }
        };

        window.addEventListener("wheel", handleWheelZoom, { passive: false });

        return () => {
            window.removeEventListener("wheel", handleWheelZoom);
        };
    }, []);

    const onLoadError = useCallback((error: any) => {
        console.error("Error while loading the File: ", error.message);
    }, []);

    const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    }, []);

    const renderDocument = useCallback(() => {
        if (!protocolStore.protocolFileURL && !protocolStore.protocolFileId) {
            return (
                <UI.Col className="text-center">
                    <div>{t("screens.assemblies.protocolcontent.no_document_found")}</div>
                </UI.Col>
            );
        } else {
            if (!protocolStore.protocolFileURL && protocolStore.isProtocolPdfLoading) {
                return (
                    <UI.Col className="text-center">
                        <UI.RotatingSpinner noLogo />
                    </UI.Col>
                );
            }
        }

        return (
            <div className={styles.DocumentDiv}>
                <Document
                    className={styles.Document}
                    file={protocolStore.protocolFileURL}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onLoadError}
                    loading={""}
                    error={""}
                    options={pdfDocumentOptions}
                >
                    {Array.from(new Array(numPages), (_, index) => (
                        <Page width={pageWidth} key={`page_${index + 1}`} pageNumber={index + 1} />
                    ))}
                </Document>
            </div>
        );
    }, [
        protocolStore.protocolFileURL,
        protocolStore.protocolFileId,
        protocolStore.isProtocolPdfLoading,
        numPages,
        onDocumentLoadSuccess,
        onLoadError,
        pageWidth,
        t
    ]);

    return (
        <UI.Card title={t("screens.assemblies.protocolcontent.preview").toString()}>
            <ProtocolDocumentButtons assemblyId={props.assemblyId} />
            {renderDocument()}
        </UI.Card>
    );
});

export const ProtocolPreview = Sentry.withProfiler(ProtocolPreviewBase);
